import * as SalespersonHelper from "helpers/SalespersonHelper";
import * as TableAreaHelper from "helpers/TableAreaHelper";
import * as ProductsHandler from "./ProductsHandler";
import * as CategoriesHandler from "./CategoriesHandler";
import * as SalesHandler from "./SaleHandler";
import * as TableHandler from "./TableHandler";
import * as ShopsHelper from "helpers/ShopsHelper";
import * as ProductHelper from "helpers/ProductHelper";
import * as SalesHelper from "helpers/SalesHelper";
import { ORDER_TYPES, SALE_STATUS } from "const";
import { v4 as uuidv4 } from "uuid";
import * as TerminalHelper from "helpers/TermialHelper";

// Handle incoming requests from native host (Windows C# / Android Kotlin)
// This function is exposed to the global window object
// Signature: window.nativeApi.handleRequest(requestId, requestData)
export const handleRequest = async (requestId, requestData) => {
  if (typeof requestData === "string") {
    requestData = JSON.parse(requestData);
  }

  try {
    const { endpoint, method, body, headers, queryParams } = requestData;

    // Health Check Endpoint (unauthenticated)
    if (endpoint.toLowerCase() === "/health") {
      sendResponse(requestId, { message: "Success", status: 200 });
      return;
    }

    // Scale Order Endpoint (unauthenticated)
    if (endpoint.toLowerCase() === "/scale/orders") {
      const result = await handleScaleOrder(body);
      sendResponse(requestId, result);
      return;
    }

    // Auth Middleware / Alias
    if (endpoint.toLowerCase() === "/auth/login" || endpoint.toLowerCase() === "/auth") {
      const result = await loginHandler(body);
      sendResponse(requestId, result);
      return;
    }

    const token = (headers?.["Authorization"] ?? headers?.["authorization"])?.replace("Bearer ", "");
    const salesPerson = await getSalespersonByToken(token);

    if (!salesPerson) {
      sendResponse(requestId, { message: "Unauthorized", status: 401 }, 401);
      return;
    }

    const result = await handleAuthenticatedRoute(endpoint, method, body, headers, queryParams, salesPerson);
    sendResponse(requestId, result);
  } catch (error) {
    console.error(`Error handling API request ${requestData?.endpoint}:`, error);
    sendError(requestId, error.message);
  }
};

// Router Logic: Maps endpoints to internal handlers
const handleAuthenticatedRoute = async (endpoint, method, body, headers, queryParams, salesPerson) => {
  if (endpoint.startsWith("/carts")) {
    return SalesHandler.handleRequest(endpoint, method, body, queryParams, salesPerson);
  }

  switch (endpoint) {
    case "/areas":
      const data = await TableAreaHelper.getAreasFromDB();
      return { data: data, message: "Area list", status: 200 };

    case "/tables":
      return TableHandler.handleRequest(endpoint, method);

    case "/categories":
      return CategoriesHandler.handleRequest(endpoint, method);

    case "/items":
    case "/products":
      return ProductsHandler.handleRequest(endpoint, method);

    case "/carts":
      return SalesHandler.handleRequest(endpoint, method, body, headers, queryParams, salesPerson);

    case "/shops":
      const shopDataList = ShopsHelper.getCachedShopProfile();
      return { status: 200, message: "Shop list", data: shopDataList ? [shopDataList] : [] };

    case "/shops/details":
      const shopData = ShopsHelper.getCachedShopProfile();
      const terminalName = await TerminalHelper.getCachedTerminal().name;
      return { status: 200, message: "Shop details", data: { ...shopData, terminal_name: terminalName } };

    case "/shops/payment-modes":
      const paymentModes = ShopsHelper.getPaymentModes();
      return { status: 200, message: "Payment modes", data: paymentModes };

    case "/orders":
      return SalesHandler.handleRequest(endpoint, method, body, headers, queryParams, salesPerson);

    default:
      throw new Error(`Route not found: ${endpoint}`);
  }
};

const postNativeMessage = (message) => {
  // Windows (WebView2)
  if (window.chrome?.webview?.postMessage) {
    window.chrome.webview.postMessage(message);
    return;
  }

  // Android (JavascriptInterface)
  if (window.AndroidBridge?.onApiResponse) {
    window.AndroidBridge.onApiResponse(JSON.stringify(message));
    return;
  }

  // iOS (WKWebView message handler)
  if (window.webkit?.messageHandlers?.iosBridge) {
    window.webkit.messageHandlers.iosBridge.postMessage({
      action: "onApiResponse",
      responseJson: JSON.stringify(message),
    });
    return;
  }

  console.warn("No native bridge available. Message not sent:", message);
};

const sendResponse = (requestId, data, status = 200) => {
  postNativeMessage({
    type: "apiResponse",
    requestId: requestId,
    data: data,
    status: status,
  });
};

const sendError = (requestId, errorMessage) => {
  postNativeMessage({
    type: "apiResponse",
    requestId: requestId,
    data: { message: errorMessage, status: 500 },
    status: 500,
  });
};

const loginHandler = async (credentials) => {
  try {
    const { username, password } = credentials || {};
    if (!username || !password) {
      return { message: "Missing credentials", status: 400 };
    }

    const user = await SalespersonHelper.getSalespersonByUsername(username);

    if (!user || user.password !== password) {
      return { message: "Invalid credentials", status: 401 }; // Or 400 as per original C#
    }

    // Generate token
    const token = uuidv4().replace(/-/g, ""); // Matches C# GUID "N" format style

    // Update user with new token
    const updatedUser = await SalespersonHelper.updateSalesPersonById(user.id, token);

    return {
      data: {
        access_code: updatedUser.access_code,
        id: updatedUser.id,
        is_active: updatedUser.is_active,
        name: updatedUser.name,
        password: updatedUser.password,
        username: updatedUser.username,
        authToken: updatedUser.authToken,
      },
      message: "Success",
      status: 200,
    };
  } catch (error) {
    console.error("Login error:", error);
    return { message: "Login failed", status: 500 };
  }
};

const getSalespersonByToken = async (token) => {
  if (!token) return null;
  return SalespersonHelper.getSalespersonByAuthToken(token);
};

const handleScaleOrder = async (body) => {
  try {
    const { items, discount = 0 } = body || {};

    // Convert items PLUs to IDs and build cart items
    const allProducts = await ProductHelper.getItemsFromDB();

    const cartItems = [];
    for (const scaleItem of items || []) {
      const product = allProducts.find((p) => String(p.plu) === String(scaleItem.plu));
      if (!product) {
        throw new Error(`Product not found for PLU: ${scaleItem.plu}`);
      }
      cartItems.push({
        id: product.id,
        qty: scaleItem.quantity,
        rate: scaleItem.price ?? product.price,
      });
    }

    // Retrieve default cash payment mode
    const paymentModes = ShopsHelper.getPaymentModes() || [];
    const cashMode = paymentModes.find((m) => m.name.toLowerCase().includes("cash") || m.id === 1) || paymentModes[0];

    const sale = {
      order_type: ORDER_TYPES.TAKE_AWAY,
      items: cartItems,
      discount_amount: discount,
      status: SALE_STATUS.PAID,
    };

    // Create new sale (this will calculate totals and save in DB)
    const createdSale = await SalesHelper.createNewSale(sale);

    // Apply cash payment
    const tenders = [
      {
        payment_mode: cashMode,
        amount: createdSale.payable,
      },
    ];

    const paidSale = await SalesHelper.applyPaymentToSale(createdSale, tenders, 0, 0);

    const finalizedSale = await SalesHelper.saveAndProcess(paidSale);
    SalesHelper.notifySalesUpdate(finalizedSale);

    return { status: 200, message: "Order created successfully", data: finalizedSale };
  } catch (error) {
    console.error("Failed to create scale order:", error);
    return { status: 500, message: error.message };
  }
};
